import { Link } from "react-router-dom";
import { ArrowRight, Building2, Scale, FileSignature, Landmark, FolderCheck, Rocket, Users, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeading, CTABand, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const COPY = {
  fr: {
    meta: "Création d'entreprise au Togo",
    eyebrow: "Création d'entreprise",
    h1a: "Créez votre entreprise",
    h1b: "au Togo, sans friction.",
    sub: "De l'idée à l'immatriculation : DOC TOGO pilote l'intégralité de vos formalités de création au Togo — que vous soyez sur place, dans la diaspora ou investisseur étranger.",
    pillarsEyebrow: "Notre accompagnement",
    pillarsTitle: "Un parcours complet, du statut au lancement.",
    pillarsSub: "Six volets pris en charge par nos équipes pour que votre entreprise voie le jour dans les règles et dans les délais.",
    stepsEyebrow: "Le déroulé",
    stepsTitle: "Votre société en quatre temps.",
    stepsSub: "Un processus rodé, piloté à distance si nécessaire, avec des points d'avancement réguliers.",
    remoteBold: "Diaspora & investisseurs :",
    remoteText: "la création peut être menée intégralement à distance. Nous préparons les mandats, vous signez où que vous soyez, nous gérons le reste sur place.",
    remoteCta: "Lancer ma création",
    whyEyebrow: "Pourquoi le Togo",
    whyTitle: "Une destination d'investissement en plein essor.",
    whyItems: [
      "Position stratégique en Afrique de l'Ouest, port en eau profonde de Lomé",
      "Création d'entreprise simplifiée via le CFE (guichet unique)",
      "Coûts de création parmi les plus compétitifs de la région",
      "Stabilité politique et cadre OHADA sécurisant les affaires",
      "Marché régional CEDEAO de plus de 400 millions de consommateurs",
    ],
    whyImgAlt: "Lomé, capitale économique du Togo",
    ctaTitle: "Votre entreprise togolaise commence ici.",
    ctaSub: "Décrivez votre projet : vous recevez sous 24h la marche à suivre, les pièces requises et un devis détaillé.",
  },
  en: {
    meta: "Company formation in Togo",
    eyebrow: "Company formation",
    h1a: "Set up your company",
    h1b: "in Togo, friction-free.",
    sub: "From idea to registration: DOC TOGO manages all your incorporation formalities in Togo — whether you are on the ground, in the diaspora or a foreign investor.",
    pillarsEyebrow: "Our support",
    pillarsTitle: "A complete journey, from statutes to launch.",
    pillarsSub: "Six workstreams handled by our teams so your company comes to life compliantly and on time.",
    stepsEyebrow: "The process",
    stepsTitle: "Your company in four stages.",
    stepsSub: "A well-honed process, managed remotely if needed, with regular progress updates.",
    remoteBold: "Diaspora & investors:",
    remoteText: "incorporation can be handled entirely remotely. We prepare the mandates, you sign wherever you are, we handle the rest on the ground.",
    remoteCta: "Start my company",
    whyEyebrow: "Why Togo",
    whyTitle: "A fast-growing investment destination.",
    whyItems: [
      "Strategic position in West Africa, deep-water port of Lomé",
      "Simplified company formation via the CFE (one-stop shop)",
      "Among the most competitive incorporation costs in the region",
      "Political stability and the OHADA framework securing business",
      "Regional ECOWAS market of over 400 million consumers",
    ],
    whyImgAlt: "Lomé, economic capital of Togo",
    ctaTitle: "Your Togolese company starts here.",
    ctaSub: "Describe your project: within 24h you receive the roadmap, required documents and a detailed quote.",
  },
};

const PILLARS_ICONS = [Scale, FolderCheck, Landmark, Building2, FileSignature, Rocket];

const PILLARS_TEXT = {
  fr: [
    { title: "Orientation sur la forme juridique", desc: "SARL, SUARL, entreprise individuelle, SA : nous vous aidons à choisir la structure adaptée à votre projet, votre capital et votre fiscalité." },
    { title: "Constitution du dossier", desc: "Liste des pièces, rédaction des statuts, vérification de conformité : votre dossier est complet dès le premier dépôt." },
    { title: "Formalités de création", desc: "Dépôt et suivi auprès du Centre de Formalités des Entreprises (CFE) et des administrations compétentes." },
    { title: "Immatriculation", desc: "Obtention du RCCM, du NIF et de tous les identifiants officiels de votre nouvelle structure." },
    { title: "Documents constitutifs", desc: "Statuts enregistrés, extraits d'immatriculation et attestations : l'ensemble des documents vous est remis." },
    { title: "Formalités post-création", desc: "Compte bancaire professionnel, autorisations sectorielles, mise en conformité : nous restons à vos côtés après le lancement." },
  ],
  en: [
    { title: "Legal structure guidance", desc: "SARL, SUARL, sole proprietorship, SA: we help you choose the structure suited to your project, capital and taxation." },
    { title: "Application file preparation", desc: "Document checklist, drafting of articles of association, compliance checks: your file is complete from the first filing." },
    { title: "Incorporation formalities", desc: "Filing and follow-up with the Centre de Formalités des Entreprises (CFE) and the competent authorities." },
    { title: "Registration", desc: "Obtaining the RCCM, NIF and all official identifiers for your new entity." },
    { title: "Constitutive documents", desc: "Registered statutes, registration extracts and certificates: all documents are handed over to you." },
    { title: "Post-incorporation formalities", desc: "Business bank account, sector licences, compliance: we stay by your side after launch." },
  ],
};

const STEPS_CE = {
  fr: [
    { num: "01", title: "Étude de votre projet", desc: "Un échange pour comprendre votre activité, vos associés et vos objectifs au Togo." },
    { num: "02", title: "Montage juridique", desc: "Choix de la forme, rédaction des statuts et préparation du dossier constitutif." },
    { num: "03", title: "Immatriculation", desc: "Dépôt au CFE, obtention du RCCM et du NIF sous 7 à 15 jours ouvrés en moyenne." },
    { num: "04", title: "Lancement serein", desc: "Remise des documents officiels et accompagnement post-création." },
  ],
  en: [
    { num: "01", title: "Project review", desc: "A discussion to understand your business, partners and goals in Togo." },
    { num: "02", title: "Legal structuring", desc: "Choice of structure, drafting of statutes and preparation of the incorporation file." },
    { num: "03", title: "Registration", desc: "CFE filing, RCCM and NIF obtained within 7 to 15 business days on average." },
    { num: "04", title: "Smooth launch", desc: "Delivery of official documents and post-incorporation support." },
  ],
};

export default function CreationEntreprise() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  return (
    <div data-testid="creation-entreprise-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.business}
        imageAlt={c.whyImgAlt}
      />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading eyebrow={c.pillarsEyebrow} title={c.pillarsTitle} sub={c.pillarsSub} />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PILLARS_TEXT[lang].map((p, i) => {
              const Icon = PILLARS_ICONS[i];
              return (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div
                    data-testid={`ce-pillar-${i}`}
                    className="h-full rounded-3xl border border-brand/10 bg-white p-7 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-light text-brand">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <section className="bg-brand-deep py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading dark eyebrow={c.stepsEyebrow} title={c.stepsTitle} sub={c.stepsSub} />
          <div className="mt-14">
            {STEPS_CE[lang].map((s, i) => (
              <Reveal key={s.num} delay={i * 0.06}>
                <div data-testid={`ce-step-${s.num}`} className="group grid grid-cols-[auto_1fr] items-baseline gap-x-6 gap-y-2 border-t border-white/10 py-8 transition-colors duration-500 hover:border-gold/40 lg:grid-cols-[160px_1fr_1.4fr]">
                  <span className="font-mono text-4xl font-bold text-white/15 transition-colors duration-500 group-hover:text-gold lg:text-5xl">{s.num}</span>
                  <h3 className="font-display text-xl font-bold text-white lg:text-2xl">{s.title}</h3>
                  <p className="col-start-2 text-sm leading-relaxed text-white/50 lg:col-start-3 lg:text-base">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col items-start gap-6 rounded-3xl border border-gold/25 bg-white/5 p-8 backdrop-blur-md lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <Users className="mt-1 h-6 w-6 shrink-0 text-gold" />
                <p className="max-w-2xl text-sm leading-relaxed text-white/70 lg:text-base">
                  <strong className="text-white">{c.remoteBold}</strong> {c.remoteText}
                </p>
              </div>
              <Link
                to="/contact?categorie=Cr%C3%A9ation%20d%27entreprise"
                data-testid="ce-remote-cta"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
              >
                {c.remoteCta}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <img src={IMAGES.lome} alt={c.whyImgAlt} loading="lazy" className="h-80 w-full rounded-[2rem] object-cover shadow-xl lg:h-[440px]" />
            </Reveal>
            <Reveal delay={0.15}>
              <SectionHeading eyebrow={c.whyEyebrow} title={c.whyTitle} />
              <ul className="mt-8 space-y-4">
                {c.whyItems.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-medium text-slate-700 lg:text-base">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand title={c.ctaTitle} sub={c.ctaSub} />
    </div>
  );
}
